import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, take, timeout } from 'rxjs/operators';
import { PublicProfile, Vehicle } from '../models/core.models';
import { Organization } from '../models/organization.models';
import { OrganizationService } from '../services/firebase/organization.service';
import { PublicProfilesFirebaseService } from '../services/firebase/public-profiles-firebase.service';
import { VehiclesFirebaseService } from '../services/firebase/vehicles-firebase.service';

/** Resolved payload for /hosts/:slug. `null` means host not found / errored. */
export interface HostProfileData {
  organization: Organization;
  host: PublicProfile | null;
  vehicles: Vehicle[];
}

/**
 * Resolves the host organization (by slug), its primary admin and its
 * vehicles for /hosts/:slug BEFORE the route renders. The page previously
 * loaded all of this inside an async ngOnInit, which ran after the SSR
 * stability snapshot — every host page flushed with the homepage <title>,
 * default meta description, og:url=/ and no <h1>, so Google canonicalised
 * the whole set to the homepage. The Router awaits resolvers before render,
 * which makes the reads deterministic under request-time SSR (same fix as
 * motorhomeSimilarResolver for /motorhome/:id).
 *
 * All reads are one-shot REST fetches (no WebChannel listeners), so they
 * complete cleanly on the server.
 */
export const hostProfileResolver: ResolveFn<HostProfileData | null> = (
  route: ActivatedRouteSnapshot,
) => {
  const organizationService = inject(OrganizationService);
  const usersService = inject(PublicProfilesFirebaseService);
  const vehiclesService = inject(VehiclesFirebaseService);

  const slug = route.paramMap.get('slug') ?? '';
  if (!slug) return of(null);

  return organizationService.getOrganizationBySlug(slug).pipe(
    switchMap((org) => {
      if (!org) return of(null);

      return forkJoin({
        host: org.primaryAdminId
          ? usersService.getByIdOnce(org.primaryAdminId).pipe(
              map((user) => user ?? null),
              catchError(() => of(null)),
            )
          : of(null),
        // One-shot org-scoped read so SSR resolves deterministically.
        // Active-only: Firestore rules reject unauthenticated queries not
        // provably limited to isActive == true (HMM-240).
        vehicles: vehiclesService
          .getActiveByOrganizationIdOnce(org.id)
          .pipe(catchError(() => of([] as Vehicle[]))),
      }).pipe(map(({ host, vehicles }) => ({ organization: org, host, vehicles: vehicles ?? [] })));
    }),
    take(1),
    timeout(8000), // Don't hang the render if Firestore is slow/unreachable.
    catchError(() => of(null)),
  );
};
